import { useState } from 'react';
import { ShieldCheck, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardBody, Input, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { Toolbar } from '@vnetwork/ui/layout';
import { useSlaDashboardServices, type SlaDashboardServiceRow } from '@/api/services';
import { EntityLink } from '@/components/entity-link';
import { TableLink } from '@/components/table-link';

/**
 * Services-with-SLA registry — a read-only list of every service that has an
 * active SLA, sourced from `GET /sla/dashboard/services` (verified live 200).
 * The endpoint returns all SLA-attached services in one page (max 500 currently
 * 14 records). Client-side search filters by service_name / customer_name.
 *
 * Each row links to the service detail → SLA tab.
 * Clicking a row navigates to `/services/:id?tab=sla`.
 */
export function SlaServicesListSection() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  // Use a 1-year rolling window by default — wide enough to capture all active SLA services
  const today = new Date();
  const endDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const startYear = today.getFullYear() - 1;
  const startDate = `${startYear}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const { data: rows = [], isLoading, isError, refetch } = useSlaDashboardServices(startDate, endDate);

  const filtered = query.trim()
    ? rows.filter(
        (r) =>
          r.service_name.toLowerCase().includes(query.toLowerCase()) ||
          r.customer_name.toLowerCase().includes(query.toLowerCase()),
      )
    : rows;

  const columns: ColumnDef<SlaDashboardServiceRow, unknown>[] = [
    {
      id: 'service_id',
      header: 'ID',
      cell: ({ row }) => (
        <TableLink to={`/services/${row.original.service_id}?tab=sla`} title={String(row.original.service_id)}>
          {row.original.service_id}
        </TableLink>
      ),
    },
    {
      id: 'service_name',
      header: 'Service',
      cell: ({ row }) => (
        <span
          style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textTransform: 'uppercase' }}
          title={row.original.service_name}
        >
          {row.original.service_name}
        </span>
      ),
    },
    {
      id: 'customer',
      header: 'Customer',
      cell: ({ row }) => (
        <EntityLink
          to={`/customers/${row.original.customer_id}`}
          id={row.original.customer_id}
          name={row.original.customer_name}
        />
      ),
    },
    {
      id: 'ss_target_uptime',
      header: 'Target uptime',
      cell: ({ row }) => `${row.original.ss_target_uptime}%`,
    },
    {
      id: 'actual_uptime',
      header: 'Actual uptime',
      cell: ({ row }) => `${row.original.actual_uptime.toFixed(3)}%`,
    },
    {
      id: 'ss_sla_downtime_minutes',
      header: 'Downtime (min)',
      cell: ({ row }) => row.original.ss_sla_downtime_minutes,
    },
    {
      id: 'ss_incident_count',
      header: 'Incidents',
      cell: ({ row }) => row.original.ss_incident_count,
    },
    {
      id: 'sla_met',
      header: 'SLA status',
      cell: ({ row }) => (
        <StatusPill tone={row.original.sla_met ? 'success' : 'danger'}>
          {row.original.sla_met ? 'Met' : 'Breached'}
        </StatusPill>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Toolbar
        style={{ marginBottom: 0 }}
        start={
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search SLA services…"
            startSlot={<Search size={15} />}
            style={{ maxWidth: 320 }}
          />
        }
      />

      {isError ? (
        <Alert
          tone="danger"
          title="Failed to load SLA services"
          actions={<Button size="sm" variant="ghost" onClick={() => refetch()}>Retry</Button>}
        />
      ) : isLoading ? (
        <Card flat>
          <CardBody>
            <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} style={{ height: 24 }} />
              ))}
            </div>
          </CardBody>
        </Card>
      ) : (
        <DataTable
          columns={columns}
          data={filtered}
          getRowId={(r) => String(r.service_id)}
          onRowClick={(r) => navigate(`/services/${r.service_id}?tab=sla`)}
          emptyState={
            <EmptyState
              icon={<ShieldCheck size={24} />}
              title="No SLA-attached services"
              description="Services with active SLA agreements will appear here."
            />
          }
        />
      )}
    </div>
  );
}
